/**
 * The model ids worth offering on the Transcription tab, per service.
 *
 * These were three plain text fields, and a plain text field is the wrong
 * control for this question: nothing on the page said which ids exist, the one
 * that matters most (`eleven_v3`, the only ElevenLabs model that speaks Estonian
 * at all) looked exactly as arbitrary as the four that do not, and the cost of a
 * typo is an announcement that previews perfectly and reaches a caller in the
 * wrong language. A list somebody picks from turns all of that into one glance.
 *
 * ⚠ SUGGESTIONS, NEVER A WHITELIST. The picker they feed is `allowCustom`, so
 * anything typed is still a value. That is not politeness: providers rename and
 * add models between our releases, and a settings page that refused an id this
 * build's list has not heard of would need a deploy to stay usable. The API
 * agrees - `clampElevenLabs` only trims and caps, and the request goes out with
 * whatever is stored.
 *
 * `estonian` is the one piece of judgement in here and it is the reason this
 * file exists rather than a literal in the component: ElevenLabs' own default
 * covers 29 languages and Estonian is not one of them, so a synthesis model
 * without that mark returns fluent audio of Estonian words in another language's
 * mouth. The list says so next to the id instead of leaving it to be discovered
 * on a phone call.
 *
 * The prose is NOT here. Each entry carries a `note` KEY under
 * `settings.transcription.modelNotes.*`, because the line under a model id is
 * copy an operator reads and this app keeps its copy in `messages/*.json`. The
 * keys are hand-named rather than derived from the id, which contains dots that
 * a dotted-path lookup would walk straight into.
 */

export interface SpeechModelChoice {
  id: string;
  /** Message key under `settings.transcription.modelNotes` for the line beneath the id. */
  note: string;
  /** Speaks Estonian. Only stated where it is known and load-bearing. */
  estonian?: boolean;
}

/**
 * Gemini models for the after-call pass, the live co-pilot and the summary.
 *
 * One field drives all three, which is why the notes talk about speed and cost
 * rather than about transcription alone: the co-pilot sends a window every
 * fifteen seconds of a live call, so a slow model is felt there long before it
 * is noticed on a finished recording.
 */
export const GEMINI_MODELS: SpeechModelChoice[] = [
  { id: "gemini-3.5-flash", note: "flash35" },
  { id: "gemini-3.5-pro", note: "pro35" },
  { id: "gemini-2.5-flash", note: "flash25" },
  { id: "gemini-2.5-pro", note: "pro25" },
  { id: "gemini-2.5-flash-lite", note: "lite25" },
];

/**
 * ElevenLabs speech-to-text (Scribe).
 *
 * The whole reason that provider is worth wiring up: Scribe covers 90+
 * languages including Estonian, which a general-purpose model handles rather
 * less well.
 */
export const SCRIBE_MODELS: SpeechModelChoice[] = [
  { id: "scribe_v2", note: "scribeV2", estonian: true },
  { id: "scribe_v1", note: "scribeV1", estonian: true },
  { id: "scribe_v1_experimental", note: "scribeV1x", estonian: true },
];

/**
 * ElevenLabs synthesis.
 *
 * ⚠ ONLY `eleven_v3` SPEAKS ESTONIAN. The other three are here so that the field
 * is honest about what exists - somebody with an English-language announcement
 * has a real reason to want Flash - and every one of them carries the warning in
 * its own row rather than in a note further down the page.
 */
export const ELEVEN_TTS_MODELS: SpeechModelChoice[] = [
  { id: "eleven_v3", note: "elevenV3", estonian: true },
  { id: "eleven_multilingual_v2", note: "elevenMulti2" },
  { id: "eleven_turbo_v2_5", note: "elevenTurbo25" },
  { id: "eleven_flash_v2_5", note: "elevenFlash25" },
];
